import React, { useEffect, useState } from 'react'
import { Box, Typography, CircularProgress, Link } from '@mui/material'
import { useAuth } from 'src/common/context'
import * as S from './styles'
import { alertNotification, Container } from 'src/ui'
import WelcomeCard from './WelcomeCard'
import MintBurnSummary from './MintBurnSummary'
import { OrdersTable } from 'src/common/components'
import { getOrders, OrderDetails } from 'src/common/api'
import { identifyUser, logEvent } from '../../common/analytics/analytics'

const Dashboard: React.FC = () => {
  const { user, socket } = useAuth()
  const [fetching, setFetching] = useState(true)
  const [orders, setOrders] = useState<OrderDetails[]>([])

  const fetchOrders = async () => {
    try {
      const response = await getOrders({ sortBy: 'NEWEST' })
      // Get the 5 most recent orders
      const recentOrders = response.orders.slice(0, 5)
      setOrders(recentOrders)
      setFetching(false)
    } catch (err) {
      alertNotification('Fetching orders failed. Please contact support.', 'error')
      setFetching(false)
    }
  }

  useEffect(() => {
    fetchOrders()

    if (!socket) return

    const handler = async () => {
      console.debug('Received orders socket update')
      // TO-DO: could be optimized to fetch order by id, backend socket returns orderId
      await fetchOrders()
    }

    socket.on('order', handler)

    return () => {
      socket.off('order', handler)
    }
  }, [socket])

  useEffect(() => {
    identifyUser(user ? user.sub : '')
    const start = performance.now()
    return () => {
      const duration = (performance.now() - start) / 1000
      logEvent('time_spent', { page: 'DashboardPage', duration_s: duration })
    }
  }, [])

  return (
    <S.DashboardContainer>
      <S.LeftSection>
        <S.SummaryContainer>
          <Box
            display="flex"
            alignItems="stretch"
            flexDirection={{ xs: 'column', md: 'row' }}
            gap="30px"
            mb="30px"
            sx={{ width: '100%' }}
          >
            <Box flex={{ xs: '1 1 100%', md: '3 1 0%' }} minWidth={0} display="flex">
              <Container height="100%" maxWidth="100%" paperPadding={30} marginTop={0} marginBottom={0}>
                <WelcomeCard firstName={user?.given_name} />
              </Container>
            </Box>
            <Box flex={{ xs: '1 1 100%', md: '2 1 0%' }} minWidth={0} display="flex">
              <Container height="100%" maxWidth="100%" paperPadding={30} marginTop={0} marginBottom={0}>
                <MintBurnSummary />
              </Container>
            </Box>
          </Box>
        </S.SummaryContainer>
        <Container maxWidth="100%" marginTop={0} marginBottom="90px" paperPadding={40}>
          <Box display="flex" mb="32px" alignItems="center" justifyContent="space-between">
            <Typography variant="h6" fontWeight={400}>
              Recent Orders
            </Typography>
            <Link href="/orders">view more</Link>
          </Box>
          {fetching ? (
            <Box display="flex" justifyContent="center">
              <CircularProgress disableShrink />
            </Box>
          ) : orders.length ? (
            <OrdersTable orders={orders} onRefresh={fetchOrders} />
          ) : (
            <Box display="flex" justifyContent="center" alignItems="center" height="100px">
              <Typography variant="h6">No Orders</Typography>
            </Box>
          )}
        </Container>
      </S.LeftSection>
    </S.DashboardContainer>
  )
}

export default Dashboard
